import React, { useMemo, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthState, useAuthActions } from '@/shared/stores/auth-store';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Пропсы для компонента AuthGuard
 */
interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Компонент защиты маршрутов
 * Проверяет аутентификацию пользователя и перенаправляет на страницу входа если необходимо
 * @param children - Дочерние компоненты для отображения если пользователь аутентифицирован
 * @returns JSX элемент
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, token } = useAuthState();
  const { verifyToken } = useAuthActions();
  const location = useLocation();

  // Проверяем токен при монтировании AuthGuard (только если есть токен)
  useEffect(() => {
    if (token && !isAuthenticated && !isLoading) {
      verifyToken();
    }
  }, [token, isAuthenticated, isLoading, verifyToken]);

  // Мемоизируем состояние навигации для предотвращения ненужных ре-рендеров
  const navigationState = useMemo(
    () => ({ from: location }),
    [location.pathname]
  );

  // Показываем загрузку во время проверки аутентификации
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Если есть токен, но еще не аутентифицированы, показываем загрузку
  // Это предотвращает flash редиректа на /login во время верификации токена
  if (token && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Если пользователь не аутентифицирован и нет токена, перенаправляем на страницу входа
  if (!isAuthenticated) {
    return <Navigate to="/login" state={navigationState} replace />;
  }

  // Если пользователь аутентифицирован, отображаем дочерние компоненты
  return <>{children}</>;
};
